import { ChangeDetectionStrategy, Component, type OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { DatePickerModule } from 'primeng/datepicker';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { CloudApiService, type CapabilityDescriptor } from '@/cloud/services/cloud-api.service';
import { SectionCardComponent } from '@/shared/ui/section-card/section-card.component';
import { EmptyStateComponent } from '@/shared/ui/empty-state/empty-state.component';

/** Namespace API keys — scoped to specific capabilities (device self-registration, package upload, report upload, AI agent read access). */
@Component({
    selector: 'app-namespace-api-keys',
    standalone: true,
    imports: [CommonModule, FormsModule, ButtonModule, CheckboxModule, DialogModule, InputTextModule, TableModule, TagModule, DatePickerModule, TooltipModule, ConfirmDialogModule, SectionCardComponent, EmptyStateComponent],
    providers: [ConfirmationService],
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrl: './namespace-section.scss',
    template: `
        <p-confirmdialog />
        <app-section-card
            title="API keys"
            subtitle="Keys are scoped to specific capabilities (device registration, package upload, report upload, AI agent access via MCP). The key value is shown once at creation."
            icon="pi pi-key"
            [count]="apiKeys().length"
            [flush]="true"
        >
            <p-button actions label="Create API key" icon="pi pi-plus" (onClick)="openCreate()" />

            <p-table class="management-table" [value]="apiKeys()" [loading]="loading()" [scrollable]="true" dataKey="id">
                <ng-template #header>
                    <tr>
                        <th>Name</th>
                        <th>Scope</th>
                        <th class="hidden md:table-cell">Created</th>
                        <th class="hidden md:table-cell">Last used</th>
                        <th>Expires</th>
                        <th>Status</th>
                        <th class="text-right"><span class="sr-only">Actions</span></th>
                    </tr>
                </ng-template>
                <ng-template #body let-key>
                    <tr>
                        <td class="font-medium">{{ key.name }}</td>
                        <td>
                            @if (key.isLegacyFullAccess) {
                                <p-tag value="Legacy" severity="secondary" [rounded]="true" pTooltip="Created before scoping existed; grants device registration, package upload and report upload — never AI agent (MCP) access" />
                            } @else {
                                <span [pTooltip]="capabilityNames(key)">{{ key.capabilities.length }} / {{ catalog().length }} capabilities</span>
                            }
                        </td>
                        <td class="hidden md:table-cell">{{ formatDate(key.createdUtc) }}</td>
                        <td class="hidden md:table-cell">{{ formatDate(key.lastUsedUtc) }}</td>
                        <td>{{ key.expiresUtc ? formatDate(key.expiresUtc) : 'Never' }}</td>
                        <td>
                            @if (key.revoked) {
                                <p-tag value="Revoked" severity="danger" [rounded]="true" />
                            } @else if (key.isValid) {
                                <p-tag value="Active" severity="success" [rounded]="true" />
                            } @else {
                                <p-tag value="Expired" severity="warn" [rounded]="true" />
                            }
                        </td>
                        <td>
                            <div class="management-row-actions">
                                @if (!key.revoked) {
                                    <p-button label="Revoke" icon="pi pi-ban" severity="danger" [text]="true" size="small" [ariaLabel]="'Revoke ' + key.name" (onClick)="revoke(key)" />
                                }
                            </div>
                        </td>
                    </tr>
                </ng-template>
                <ng-template #emptymessage>
                    <tr>
                        <td colspan="7">
                            <app-empty-state icon="pi pi-key" title="No API keys yet" description="Create one to let devices or CI pipelines authenticate." [compact]="true" />
                        </td>
                    </tr>
                </ng-template>
            </p-table>
        </app-section-card>

        <p-dialog header="Create API key" [visible]="showCreate()" [modal]="true" [closable]="!newKeyResult()" (visibleChange)="onCreateDialogVisible($event)" [style]="{ width: '34rem' }" appendTo="body">
            @if (!newKeyResult()) {
                <div class="ns-dialog-body">
                    <div class="ns-field">
                        <label for="api-key-name">Name</label>
                        <input id="api-key-name" pInputText [(ngModel)]="newKeyName" placeholder="e.g. Production floor devices" class="w-full" [disabled]="creating()" />
                        <small>A descriptive name so you can tell keys apart later.</small>
                    </div>

                    <div class="ns-field">
                        <label>Capabilities</label>
                        <small>Grant only what this key needs.</small>
                        @for (cap of catalog(); track cap.id) {
                            <div class="ns-capability">
                                <p-checkbox [binary]="true" [inputId]="cap.id" [ngModel]="selectedCaps().has(cap.id)" (ngModelChange)="toggleCap(cap.id, $event)" [disabled]="creating()" />
                                <label [for]="cap.id">
                                    <span class="font-medium">{{ cap.label }}</span>
                                    <span>{{ cap.description }}</span>
                                </label>
                            </div>
                        }
                    </div>

                    <div class="ns-field">
                        <label for="api-key-expiry">Expiry (optional)</label>
                        <p-datepicker inputId="api-key-expiry" [(ngModel)]="newKeyExpiry" [showClear]="true" [minDate]="minExpiry" dateFormat="dd/mm/yy" placeholder="Never expires" class="w-full" appendTo="body" />
                    </div>
                </div>
            } @else {
                <div class="ns-dialog-body">
                    <div class="ns-callout">
                        <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
                        <span><strong>Copy this key now.</strong> You won't be able to see it again.</span>
                    </div>
                    <div class="ns-copy-key">
                        <input pInputText [value]="newKeyResult()!.apiKey" readonly aria-label="New API key" />
                        <p-button icon="pi pi-copy" [outlined]="true" pTooltip="Copy to clipboard" ariaLabel="Copy API key to clipboard" (onClick)="copy(newKeyResult()!.apiKey)" />
                    </div>
                    @if (newKeyResult()!.expiresUtc) {
                        <p class="ns-meta">Expires {{ formatDate(newKeyResult()!.expiresUtc) }}</p>
                    }
                </div>
            }
            <ng-template #footer>
                @if (!newKeyResult()) {
                    <p-button label="Cancel" [text]="true" (onClick)="showCreate.set(false)" [disabled]="creating()" />
                    <p-button label="Create" icon="pi pi-check" (onClick)="create()" [loading]="creating()" [disabled]="creating() || !newKeyName().trim() || selectedCaps().size === 0" />
                } @else {
                    <p-button label="Done" icon="pi pi-check" (onClick)="closeCreate()" />
                }
            </ng-template>
        </p-dialog>
    `
})
export class NamespaceApiKeys implements OnInit {
    private api = inject(CloudApiService);
    private route = inject(ActivatedRoute);
    private confirmationService = inject(ConfirmationService);
    private messageService = inject(MessageService);

    private namespaceId = signal('');
    apiKeys = signal<any[]>([]);
    loading = signal(false);
    catalog = signal<CapabilityDescriptor[]>([]);

    showCreate = signal(false);
    creating = signal(false);
    newKeyName = signal('');
    newKeyExpiry: Date | null = null;
    newKeyResult = signal<any | null>(null);
    selectedCaps = signal<Set<string>>(new Set());
    minExpiry = new Date();

    ngOnInit(): void {
        this.namespaceId.set(this.route.parent?.snapshot.paramMap.get('namespaceId') ?? '');
        this.refresh();
        this.api
            .getApiKeyCapabilityCatalog()
            .then((catalog) => this.catalog.set(catalog))
            .catch((err) => this.toastError('Failed to load capability catalog', err));
    }

    refresh() {
        if (!this.namespaceId()) return;
        this.loading.set(true);
        this.api
            .getNamespaceApiKeys(this.namespaceId())
            .then((keys) => this.apiKeys.set(keys ?? []))
            .catch((err) => this.toastError('Failed to load API keys', err))
            .finally(() => this.loading.set(false));
    }

    /** The granted capabilities by label, for the scope cell's tooltip. */
    capabilityNames(key: { capabilities?: string[] }): string {
        const labels = new Map(this.catalog().map((c) => [c.id, c.label]));
        return (key.capabilities ?? []).map((id) => labels.get(id) ?? id).join(', ');
    }

    openCreate() {
        this.newKeyName.set('');
        this.newKeyExpiry = null;
        this.newKeyResult.set(null);
        this.selectedCaps.set(new Set());
        this.showCreate.set(true);
    }

    onCreateDialogVisible(visible: boolean) {
        this.showCreate.set(visible);
        if (!visible && this.newKeyResult()) this.closeCreate();
    }

    toggleCap(capId: string, checked: boolean) {
        const next = new Set(this.selectedCaps());
        if (checked) next.add(capId);
        else next.delete(capId);
        this.selectedCaps.set(next);
    }

    create() {
        this.creating.set(true);
        const expiresUtc = this.newKeyExpiry ? this.newKeyExpiry.toISOString() : undefined;
        const capabilities = Array.from(this.selectedCaps());
        this.api
            .createNamespaceApiKey(this.namespaceId(), this.newKeyName().trim(), capabilities, expiresUtc)
            .then((result) => this.newKeyResult.set(result))
            .catch((err) => this.toastError('Create Failed', err))
            .finally(() => this.creating.set(false));
    }

    closeCreate() {
        this.showCreate.set(false);
        this.newKeyResult.set(null);
        this.refresh();
    }

    copy(text: string) {
        navigator.clipboard
            .writeText(text)
            .then(() => this.messageService.add({ severity: 'success', summary: 'Copied', detail: 'API key copied to clipboard' }))
            .catch(() => this.messageService.add({ severity: 'error', summary: 'Copy Failed', detail: 'Could not access the clipboard' }));
    }

    revoke(key: any) {
        this.confirmationService.confirm({
            header: 'Revoke API Key',
            message: `Revoke "${key.name}"? Devices using it will no longer be able to self-register. This cannot be undone.`,
            icon: 'pi pi-exclamation-triangle',
            acceptButtonStyleClass: 'p-button-danger',
            acceptLabel: 'Revoke',
            accept: () => {
                this.api
                    .revokeApiKey(key.id)
                    .then(() => {
                        this.messageService.add({ severity: 'success', summary: 'Key Revoked', detail: key.name });
                        this.refresh();
                    })
                    .catch((err) => this.toastError('Revoke Failed', err));
            }
        });
    }

    formatDate(dateStr: string | null | undefined): string {
        if (!dateStr) return 'Never';
        try {
            return new Date(dateStr).toLocaleString();
        } catch {
            return dateStr;
        }
    }

    private toastError(summary: string, err: any) {
        const detail = err?.error?.message ?? (typeof err?.error === 'string' ? err.error : null) ?? err?.message ?? 'An error occurred';
        this.messageService.add({ severity: 'error', summary, detail });
    }
}
